/**
 * Authentication management mixin
 * Handles login, registration, and authentication UI
 */
import { markRaw } from 'vue';

export default {
    data() {
        return {
            show_auth_modal: false,
            auth_mode: 'login', // 'login' or 'register'
            auth_form: {
                email: '',
                password: '',
                confirm_password: ''
            },
            auth_errors: {},
            auth_loading: false,
            google_auth_url: null,
            show_email_verification_banner: false,
            verification_email_sent: false,
            password_reset_modal: false,
            password_reset_email: '',
            password_reset_sent: false,
            notification_modal: {
                show: false,
                message: '',
                type: 'info',
                auto_close: false
            },
            pending_autosave_recovery: null
        };
    },

    mounted() {
        this.init_google_auth();
        // this.check_initial_auth_state(); // Removed: Duplicate call, autologin handled in App.vue initialize_app_sequence
    },

    methods: {
        async init_google_auth() {
            try {
                const form_data = new FormData();
                form_data.append('request', 'get_google_auth_url');
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status === 'success') {
                    this.google_auth_url = response.data.auth_url;
                }
            } catch (error) {
                console.error('Error getting Google auth URL:', error);
            }
        },

        async check_initial_auth_state() {
            // Check if user should be logged in
            await this.check_login_status();
            
            // If not logged in and no cookie consent, maybe show auth prompt
            // This depends on the business logic you decide
        },

        show_login_modal() {
            this.auth_mode = 'login';
            this.reset_auth_form();
            this.show_auth_modal = true;
        },

        show_register_modal() {
            this.auth_mode = 'register';
            this.reset_auth_form();
            this.show_auth_modal = true;
        },

        close_auth_modal() {
            this.show_auth_modal = false;
            this.reset_auth_form();
        },

        reset_auth_form() {
            this.auth_form = {
                email: '',
                password: '',
                confirm_password: ''
            };
            this.auth_errors = {};
            this.password_reset_email = '';
            this.auth_loading = false;
        },

        validate_auth_form() {
            this.auth_errors = {};
            
            // Email validation
            if (!this.auth_form.email) {
                this.auth_errors.email = this.get_t('auth.email_required');
            } else if (!this.is_valid_email(this.auth_form.email)) {
                this.auth_errors.email = this.get_t('auth.invalid_email');
            }
            
            // Password validation
            if (!this.auth_form.password) {
                this.auth_errors.password = this.get_t('auth.password_required');
            } else if (this.auth_form.password.length < 6) {
                this.auth_errors.password = this.get_t('auth.password_min_length');
            }
            
            // Confirm password validation (only for register)
            if (this.auth_mode === 'register') {
                if (!this.auth_form.confirm_password) {
                    this.auth_errors.confirm_password = this.get_t('auth.confirm_password_required');
                } else if (this.auth_form.password !== this.auth_form.confirm_password) {
                    this.auth_errors.confirm_password = this.get_t('auth.passwords_not_match');
                }
            }
            
            return Object.keys(this.auth_errors).length === 0;
        },

        is_valid_email(email) {
            const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return email_regex.test(email);
        },

        async handle_auth_submit() {
            if (!this.validate_auth_form()) {
                return;
            }
            
            this.auth_loading = true;
            
            try {
                const form_data = new FormData();
                form_data.append('request', this.auth_mode === 'login' ? 'login_user' : 'register_user');
                form_data.append('email', this.auth_form.email.trim());
                form_data.append('password', this.auth_form.password);
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status === 'success') {
                    if (this.auth_mode === 'register') {
                        // Registration successful but requires email verification
                        this.show_notification_modal(response.data.message, 'success');
                        this.close_auth_modal();
                        
                        // Show email verification banner if email was sent
                        if (response.data.verification_email_sent) {
                            this.show_email_verification_banner = true;
                        }
                        
                        // Switch to login mode for next time modal is opened
                        this.auth_mode = 'login';
                    } else {
                        // Login successful
                        this.user_data = markRaw(Object.freeze({
                            email: response.data.user.email,
                            display_name: response.data.user.display_name,
                            account_status: response.data.user.account_status || 'active',
                            subscription_status: response.data.user.subscription_status || 'inactive',
                            cancel_at_period_end: response.data.user.cancel_at_period_end || false,
                            is_premium: response.data.user.is_premium || false,
                            email_verified: response.data.user.email_verified || false
                        }));
                        
                        // Save authentication status to localStorage for Mikogen landing page check
                        if (!this.comic_mode) {
                            localStorage.setItem('mikogen_auth_status', 'true');
                        }

                        // Load user preferences from login response
                        if (typeof this.load_preferences_from_response === 'function') {
                            this.load_preferences_from_response(response.data.preferences);
                        }
                        
                        this.show_notification_modal(this.get_t('auth.login_successful'), 'success');
                        this.close_auth_modal();
                        
                        // If there's a pending autosave recovery, execute it now
                        if (this.pending_autosave_recovery) {
                            const autosave = this.pending_autosave_recovery;
                            this.pending_autosave_recovery = null;
                            this.restore_from_autosave(autosave);
                            this.close_popup();
                        }
                        
                        // Refresh premium status
                        await this.check_premium_status();
                    }
                } else {
                    // Check for email verification error
                    if (response.data.error_type === 'email_not_verified') {
                        this.handle_login_email_not_verified(response.data.email);
                        return;
                    }
                    
                    // Check for Google user error
                    if (response.data.error_type === 'google_user') {
                        this.auth_errors.general = this.get_t('auth.google_user_password_reset') + ' ' + 
                                                  this.get_t('auth.use_google_login');
                        return;
                    }
                    
                    // Show error
                    this.auth_errors.general = response.data.message;
                }
            } catch (error) {
                console.error('Auth error:', error);
                this.auth_errors.general = this.get_t('auth.connection_error');
            } finally {
                this.auth_loading = false;
            }
        },

        async check_login_status() {
            try {
                const form_data = new FormData();
                form_data.append('request', 'check_php_session');
                form_data.append('language', this.current_language || 'en');
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status === 'success') {
                    this.user_data = markRaw(Object.freeze({
                        email: response.data.user.email,
                        display_name: response.data.user.display_name,
                        account_status: response.data.user.account_status || 'active',
                        subscription_status: response.data.user.subscription_status || 'inactive',
                        cancel_at_period_end: response.data.user.cancel_at_period_end || false,
                        is_premium: response.data.user.is_premium || false,
                        email_verified: response.data.user.email_verified || false
                    }));
                    
                    // Load user preferences from session response
                    if (typeof this.load_preferences_from_response === 'function') {
                        this.load_preferences_from_response(response.data.preferences);
                    }

                    // Save authentication status to localStorage for Mikogen landing page check
                    if (!this.comic_mode) {
                        localStorage.setItem('mikogen_auth_status', 'true');
                    }
                    
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Error checking login status:', error);
                return false;
            }
        },

        async logout() {
            try {
                const form_data = new FormData();
                form_data.append('request', 'logout_user');
                
                await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                // Reset user data (protected)
                this.user_data = markRaw(Object.freeze({
                    email: null,
                    display_name: null,
                    account_status: 'inactive',
                    subscription_status: 'inactive',
                    is_premium: false
                }));
                
                // Clear authentication status from localStorage
                if (!this.comic_mode) {
                    localStorage.removeItem('mikogen_auth_status');
                }
                
                // Reset preferences to defaults
                if (typeof this.initialize_default_preferences === 'function') {
                    await this.initialize_default_preferences();
                }
                
                // Show notification about preferences reset (only in Comic Editor mode)
                if (this.comic_mode && typeof this.show_notification_modal === 'function') {
                    this.show_notification_modal(this.get_t('auth.preferences_reset_on_logout'), 'info');
                }
                
                this.status_message = this.get_t('auth.logout_successful');
            } catch (error) {
                console.error('Logout error:', error);
            }
        },

        open_google_auth() {
            if (this.google_auth_url) {
                window.location.href = this.google_auth_url;
            }
        },

        switch_auth_mode() {
            this.auth_mode = this.auth_mode === 'login' ? 'register' : 'login';
            this.reset_auth_form();
        },

        // Email verification methods
        async resend_verification_email() {
            if (!this.user || !this.user.email) {
                return;
            }
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'resend_verification');
                form_data.append('email', this.user.email);
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status === 'success') {
                    this.verification_email_sent = true;
                    this.show_notification_modal(this.get_t('auth.verification_email_sent'), 'success');
                } else {
                    this.show_notification_modal(response.data.message || 'Error sending verification email', 'error');
                }
            } catch (error) {
                console.error('Error resending verification email:', error);
                this.show_notification_modal(this.get_t('auth.connection_error'), 'error');
            }
        },
        
        show_password_reset_modal() {
            this.password_reset_modal = true;
            this.password_reset_email = '';
            this.password_reset_sent = false;
        },
        
        hide_password_reset_modal() {
            this.password_reset_modal = false;
            this.password_reset_email = '';
            this.password_reset_sent = false;
        },
        
        async send_password_reset() {
            if (!this.password_reset_email || !this.is_valid_email(this.password_reset_email)) {
                this.show_notification_modal(this.get_t('auth.invalid_email'), 'error');
                return;
            }
            
            this.auth_loading = true;
            
            try {
                const form_data = new FormData();
                form_data.append('request', 'request_password_reset');
                form_data.append('email', this.password_reset_email.trim());
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status === 'success') {
                    this.password_reset_sent = true;
                } else {
                    this.show_notification_modal(response.data.message, 'error');
                }
            } catch (error) {
                console.error('Error sending password reset:', error);
                this.show_notification_modal(this.get_t('auth.connection_error'), 'error');
            } finally {
                this.auth_loading = false;
            }
        },
        
        handle_login_email_not_verified(email) {
            this.show_email_not_verified_modal(email);
        },
        
        show_email_not_verified_modal(email) {
            this.show_notification_modal(this.get_t('auth.email_not_verified'), 'warning');
            this.show_email_verification_banner = true;
        },
        
        dismiss_verification_banner() {
            this.show_email_verification_banner = false;
        },
        
        check_email_verification_status() {
            if (this.user && this.user.email_verified) {
                this.show_email_verification_banner = false;
                this.verification_email_sent = false;
            }
        },
        
        show_notification_modal(message, type = 'info', auto_close = false) {
            this.notification_modal.message = message;
            this.notification_modal.type = type;
            this.notification_modal.show = true;
            this.notification_modal.auto_close = auto_close;
            
            if (auto_close) {
                setTimeout(() => {
                    this.hide_notification_modal();
                }, 1500);
            }
        },
        
        hide_notification_modal() {
            this.notification_modal.show = false;
            this.notification_modal.message = '';
            this.notification_modal.type = 'info';
            this.notification_modal.auto_close = false;
        }
    },

    computed: {
        user_email_verified() {
            return this.user && this.user.email_verified;
        },
        
        should_show_verification_banner() {
            return this.user && !this.user_email_verified && !this.verification_email_sent;
        },
        
        is_google_user() {
            return this.user && this.user.google_id;
        }
    },
    
    watch: {
        user: {
            handler(new_user) {
                if (new_user && new_user.email_verified) {
                    this.show_email_verification_banner = false;
                    this.verification_email_sent = false;
                }
            },
            deep: true
        }
    }
};
